import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useSocket } from '@/lib/socket';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from 'lucide-react';

interface WorkOrder {
  id: string;
  orderID: string;
  index: string;
  fabricType: string;
  frameColorVaries: boolean;
  frameColor: string;
  buildoutFrameColor: string;
  hemBarFrameColor: string;
  housingFrameColor: string;
  retentionFrameColor: string;
  kittingComplete: boolean;
  fabricComplete: boolean;
  metalComplete: boolean;
  finalAssemblyComplete: boolean;
  isTKS0026?: boolean;
}

interface OtherItem {
  displayName: string;
  quantity: number;
  details: string;
  index: string;
  isReady: boolean;
}

interface SalesOrder {
  id: string;
  orderNumber: string;
  date: string;
  customer: string;
  location: string;
  workOrders: WorkOrder[];
  otherItems: OtherItem[];
}

interface CalendarItem {
  id: string;
  orderID: string;
  customer: string;
  location: string;
  status: 'queue' | 'in-process' | 'done (not shipped)';
  baseOrderNumber: string;
  fabricType?: string;
  frameColor?: string;
  frameColorVaries?: boolean;
  housingFrameColor?: string;
  buildoutFrameColor?: string;
  hemBarFrameColor?: string;
  retentionFrameColor?: string;
  kittingComplete: boolean;
  fabricComplete: boolean;
  metalComplete: boolean;
  finalAssemblyComplete: boolean;
  isWorkOrder: boolean;
  displayName?: string;
  quantity?: number;
  details?: string;
  isTKS0026?: boolean;
  scheduledDate?: string;
}

interface CalendarViewProps {
  selectedLocation?: string | null;
}

export function CalendarView({ selectedLocation }: CalendarViewProps) {
  const socket = useSocket();
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedItem, setDraggedItem] = useState<CalendarItem | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  // Debug socket connection
  useEffect(() => {
    console.log('📅 CalendarView socket state:', {
      socket: !!socket,
      connected: socket?.connected,
      id: socket?.id
    });
    
    // Additional debugging
    if (socket) {
      console.log('📅 Socket object exists, checking connection status');
      
      // Test connection with a simple emit
      if (socket.connected) {
        console.log('📅 Socket is connected, testing with requestNetSuiteData');
        socket.emit('requestNetSuiteData');
      } else {
        console.log('📅 Socket not yet connected, will wait for connect event');
      }
    } else {
      console.log('📅 No socket object available');
    }
  }, [socket]);

  useEffect(() => {
    if (socket) {
      const handleNetSuiteData = (data: SalesOrder[]) => {
        console.log('📅 CalendarView received NetSuite data:', data);
        
        const allItems: CalendarItem[] = [];
        
        if (Array.isArray(data)) {
          data.forEach(salesOrder => {
            const baseOrderNumber = salesOrder.orderNumber;
            
            // Process work orders
            if (Array.isArray(salesOrder.workOrders)) {
              salesOrder.workOrders.forEach((wo: WorkOrder) => {
                allItems.push({
                  id: wo.id,
                  orderID: wo.orderID,
                  customer: salesOrder.customer,
                  location: salesOrder.location || 'No Location',
                  status: 'queue',
                  baseOrderNumber,
                  fabricType: wo.fabricType,
                  frameColor: wo.frameColor,
                  frameColorVaries: wo.frameColorVaries,
                  housingFrameColor: wo.housingFrameColor,
                  buildoutFrameColor: wo.buildoutFrameColor,
                  hemBarFrameColor: wo.hemBarFrameColor,
                  retentionFrameColor: wo.retentionFrameColor,
                  kittingComplete: wo.kittingComplete,
                  fabricComplete: wo.fabricComplete,
                  metalComplete: wo.metalComplete,
                  finalAssemblyComplete: wo.finalAssemblyComplete,
                  isWorkOrder: true,
                  isTKS0026: wo.isTKS0026
                });
              });
            }
            
            // Process other items
            if (Array.isArray(salesOrder.otherItems)) {
              salesOrder.otherItems.forEach((item: OtherItem) => {
                allItems.push({
                  id: `${salesOrder.id}-${item.index}`,
                  orderID: salesOrder.orderNumber,
                  customer: salesOrder.customer,
                  location: salesOrder.location || 'No Location',
                  status: 'queue',
                  baseOrderNumber,
                  kittingComplete: item.isReady,
                  fabricComplete: item.isReady,
                  metalComplete: item.isReady,
                  finalAssemblyComplete: item.isReady,
                  isWorkOrder: false,
                  displayName: item.displayName,
                  quantity: item.quantity,
                  details: item.details
                });
              });
            }
          });

          // Update status for items
          allItems.forEach(item => {
            if (item.isTKS0026) {
              if (item.metalComplete && item.finalAssemblyComplete) {
                item.status = 'done (not shipped)';
              } else if (item.metalComplete || item.finalAssemblyComplete) {
                item.status = 'in-process';
              }
            } else {
              if (item.kittingComplete && item.fabricComplete && item.metalComplete && item.finalAssemblyComplete) {
                item.status = 'done (not shipped)';
              } else if (item.kittingComplete || item.fabricComplete || item.metalComplete || item.finalAssemblyComplete) {
                item.status = 'in-process';
              }
            }
          });
        }

        setItems(allItems);
        
        // Load existing schedules after items are loaded and socket is confirmed connected
        if (socket.connected) {
          console.log('📅 Requesting schedules...');
          socket.emit('getSchedules', {});
        } else {
          console.log('📅 Socket not connected, waiting...');
          // Wait for connection and then request schedules
          socket.once('connect', () => {
            console.log('📅 Socket connected, now requesting schedules');
            socket.emit('getSchedules', {});
          });
        }
        setIsLoading(false);
      };

      const handleSchedulesData = (schedules: any[]) => {
        console.log('📅 Received schedules from database:', schedules);
        
        setItems(prevItems => {
          return prevItems.map(item => {
            const schedule = schedules.find(s => s.itemId === item.id);
            if (schedule) {
              return {
                ...item,
                scheduledDate: schedule.scheduledDate ? schedule.scheduledDate.split('T')[0] : undefined
              };
            }
            return item;
          });
        });
      };

      const handleScheduleUpdated = (data: { itemId: string; scheduledDate: string | null; updatedBy: string }) => {
        console.log('📅 Schedule updated by another user:', data);
        
        setItems(prevItems => {
          return prevItems.map(item => {
            if (item.id === data.itemId) {
              return {
                ...item,
                scheduledDate: data.scheduledDate ? data.scheduledDate.split('T')[0] : undefined
              };
            }
            return item;
          });
        });

        toast({
          title: 'Schedule Updated',
          description: `${data.updatedBy} updated a schedule`,
        });
      };

      socket.on('connect', () => {
        console.log('📅 CalendarView socket connected, ID:', socket.id);
        toast({
          title: 'Connected',
          description: 'Connected to server successfully',
        });
        // Request data immediately on connect
        console.log('📅 Emitting requestNetSuiteData on connect...');
        socket.emit('requestNetSuiteData');
      });

      socket.on('disconnect', () => {
        console.log('📅 CalendarView socket disconnected');
        toast({
          title: 'Disconnected',
          description: 'Lost connection to server',
          variant: 'destructive',
        });
      });

      socket.on('netsuiteData', handleNetSuiteData);
      socket.on('schedulesData', handleSchedulesData);
      socket.on('scheduleUpdated', handleScheduleUpdated);
      
      socket.on('netsuiteError', (error) => {
        console.error('📅 NetSuite error:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        setIsLoading(false);
      });

      socket.on('scheduleUpdateError', (error) => {
        console.error('📅 Schedule update error:', error);
        toast({
          title: 'Schedule Error',
          description: error.message,
          variant: 'destructive',
        });
      });

      socket.on('scheduleUpdateSuccess', (result) => {
        console.log('📅 Schedule update successful:', result);
      });

      socket.on('scheduleFetchError', (error) => {
        console.error('📅 Schedule fetch error:', error);
        toast({
          title: 'Schedule Fetch Error',
          description: error.message,
          variant: 'destructive',
        });
      });

      // If socket is already connected when this effect runs, request data immediately
      if (socket.connected) {
        console.log('📅 Socket already connected, requesting data immediately');
        socket.emit('requestNetSuiteData');
      }

      const loadingTimeout = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          toast({
            title: 'Error',
            description: 'Failed to load data. Please try refreshing the page.',
            variant: 'destructive',
          });
        }
      }, 10000);

      return () => {
        socket.off('netsuiteData', handleNetSuiteData);
        socket.off('schedulesData', handleSchedulesData);
        socket.off('scheduleUpdated', handleScheduleUpdated);
        socket.off('netsuiteError');
        socket.off('scheduleUpdateError');
        socket.off('scheduleUpdateSuccess');
        socket.off('scheduleFetchError');
        socket.off('connect');
        socket.off('disconnect');
        clearTimeout(loadingTimeout);
      };
    }
  }, [socket, toast, isLoading]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: CalendarItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, date: string) => {
    e.preventDefault();
    if (draggedItem) {
      const newItems = items.map(item =>
        item.id === draggedItem.id
          ? { ...item, scheduledDate: date }
          : item
      );
      setItems(newItems);
      
      // Save to database
      saveScheduleToDatabase(draggedItem.id, date);
      
      setDraggedItem(null);
      toast({
        title: 'Item Scheduled',
        description: `${getItemDisplayName(draggedItem)} scheduled for ${new Date(date).toLocaleDateString()}`,
      });
    }
  };

  const handleRemoveFromSchedule = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggedItem) {
      const newItems = items.map(item =>
        item.id === draggedItem.id
          ? { ...item, scheduledDate: undefined }
          : item
      );
      setItems(newItems);
      
      // Remove from database
      saveScheduleToDatabase(draggedItem.id, null);
      
      setDraggedItem(null);
      toast({
        title: 'Item Unscheduled',
        description: `${getItemDisplayName(draggedItem)} removed from schedule`,
      });
    }
  };

  const saveScheduleToDatabase = async (itemId: string, scheduledDate: string | null) => {
    try {
      console.log('📅 saveScheduleToDatabase called:', { itemId, scheduledDate, socket: !!socket, connected: socket?.connected });
      
      if (!socket) {
        console.error('📅 No socket available');
        toast({
          title: 'Connection Error',
          description: 'No connection to server. Please refresh the page.',
          variant: 'destructive',
        });
        return;
      }

      if (!socket.connected) {
        console.error('📅 Socket not connected');
        toast({
          title: 'Connection Error',
          description: 'Not connected to server. Please refresh the page.',
          variant: 'destructive',
        });
        return;
      }

      // Find the item to get its metadata
      const item = items.find(i => i.id === itemId);
      if (!item) {
        console.error('Item not found for scheduling:', itemId);
        return;
      }

      const scheduleData = {
        itemId,
        scheduledDate,
        itemType: item.isWorkOrder ? 'work_order' as const : 'other_item' as const,
        orderNumber: item.baseOrderNumber,
        workOrderNumber: item.isWorkOrder ? item.orderID : undefined
      };

      console.log('📅 Sending schedule update:', scheduleData);
      socket.emit('updateSchedule', scheduleData);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to save schedule to database',
        variant: 'destructive',
      });
    }
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const closeDateDetail = () => {
    setSelectedDate(null);
  };

  const getItemDisplayName = (item: CalendarItem) => {
    if (item.isWorkOrder) {
      return `${item.baseOrderNumber}-${getItemDisplayNumber(item)}`;
    } else {
      return item.displayName || `${item.baseOrderNumber}-${getItemDisplayNumber(item)}`;
    }
  };

  const getItemDisplayNumber = (item: CalendarItem) => {
    if (item.isWorkOrder) {
      // Extract the number part from orderID (e.g., "SO123456-1" -> "1")
      const parts = item.orderID.split('-');
      return parts.length > 1 ? parts[parts.length - 1] : item.orderID;
    } else {
      // For other items, use the index or a sequential number
      return item.id.split('-').pop() || '1';
    }
  };

  const getCurrentWeekDays = () => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const monday = new Date(today);
    monday.setDate(today.getDate() - currentDay + 1); // Get Monday of current week
    
    const weekDays = [];
    for (let i = 0; i < 5; i++) { // Monday to Friday
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      weekDays.push(day);
    }
    return weekDays;
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getItemsForDate = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    let filteredItems = items.filter(item => item.scheduledDate === dateString);
    
    // Apply location filter
    if (selectedLocation && selectedLocation !== 'All, USA') {
      filteredItems = filteredItems.filter(item => {
        const lastSpaceIndex = item.location.lastIndexOf(' ');
        const city = item.location.slice(0, lastSpaceIndex).trim();
        const state = item.location.slice(lastSpaceIndex + 1).trim();
        return `${city}, ${state}` === selectedLocation;
      });
    }
    
    return filteredItems;
  };

  const getUnscheduledItems = () => {
    let filteredItems = items.filter(item => !item.scheduledDate);
    
    // Apply location filter
    if (selectedLocation && selectedLocation !== 'All, USA') {
      filteredItems = filteredItems.filter(item => {
        const lastSpaceIndex = item.location.lastIndexOf(' ');
        const city = item.location.slice(0, lastSpaceIndex).trim();
        const state = item.location.slice(lastSpaceIndex + 1).trim();
        return `${city}, ${state}` === selectedLocation;
      });
    }
    
    return filteredItems;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isCompleted = (item: CalendarItem) => {
    if (item.isTKS0026) {
      return item.metalComplete && item.finalAssemblyComplete;
    }
    return item.kittingComplete && item.fabricComplete && item.metalComplete && item.finalAssemblyComplete;
  };

  const renderScheduledItem = (item: CalendarItem, isInWeekView = false) => (
    <div
      key={item.id}
      draggable
      onDragStart={(e) => handleDragStart(e, item)}
      className={`p-1 mb-1 border rounded cursor-move text-xs ${
        isCompleted(item) 
          ? 'bg-green-50 border-green-200 line-through opacity-75' 
          : 'bg-white border-gray-200 hover:shadow-sm'
      } ${isInWeekView ? 'w-full' : ''}`}
    >
      <div className="font-medium text-gray-900 truncate">
        {getItemDisplayName(item)}
      </div>
      {isInWeekView && (
        <>
          <div className="text-gray-600 text-xs truncate">{item.customer}</div>
          <div className="text-gray-500 text-xs truncate">{item.location}</div>
          {item.isWorkOrder && (
            <div className="flex gap-0.5 mt-1">
              {!item.isTKS0026 && (
                <>
                  <div className={`w-1.5 h-1.5 rounded-full ${item.kittingComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Kitting"></div>
                  <div className={`w-1.5 h-1.5 rounded-full ${item.fabricComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Fabric"></div>
                </>
              )}
              <div className={`w-1.5 h-1.5 rounded-full ${item.metalComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Metal"></div>
              <div className={`w-1.5 h-1.5 rounded-full ${item.finalAssemblyComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Assembly"></div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderItem = (item: CalendarItem, isInCalendar = false) => (
    <div
      key={item.id}
      draggable
      onDragStart={(e) => handleDragStart(e, item)}
      className={`p-2 mb-1 border rounded cursor-move text-xs ${
        isCompleted(item) 
          ? 'bg-green-50 border-green-200 line-through opacity-75' 
          : 'bg-white border-gray-200 hover:shadow-sm'
      } ${isInCalendar ? 'w-full' : ''}`}
    >
      <div className="font-medium text-gray-900">
        {item.isWorkOrder 
          ? (item.isTKS0026 ? 'Buildout: ' : 'Screen Unit: ') + item.orderID
          : item.displayName + (item.quantity && item.quantity > 1 ? ` (${item.quantity})` : '')
        }
      </div>
      <div className="text-gray-600">#{item.baseOrderNumber}</div>
      <div className="text-gray-500">{item.customer}</div>
      {item.isWorkOrder && (
        <div className="flex gap-0.5 mt-1">
          {!item.isTKS0026 && (
            <>
              <div className={`w-1.5 h-1.5 rounded-full ${item.kittingComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Kitting"></div>
              <div className={`w-1.5 h-1.5 rounded-full ${item.fabricComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Fabric"></div>
            </>
          )}
          <div className={`w-1.5 h-1.5 rounded-full ${item.metalComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Metal"></div>
          <div className={`w-1.5 h-1.5 rounded-full ${item.finalAssemblyComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Assembly"></div>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  const days = getDaysInMonth(currentDate);
  const unscheduledItems = getUnscheduledItems();
  const selectedDateItems = selectedDate ? getItemsForDate(selectedDate) : [];
  const weekDays = getCurrentWeekDays();

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-4 h-4" />
              <CardTitle className="text-lg">Production Calendar</CardTitle>
              <h2 className="text-lg font-semibold">{formatMonthYear(currentDate)}</h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth('prev')}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToToday}
                className="min-w-[60px] text-xs"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth('next')}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Current Week View */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Current Week</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2">
            {weekDays.map((day) => {
              const dayItems = getItemsForDate(day);
              const isToday = day.toDateString() === new Date().toDateString();
              
              return (
                <div key={day.toISOString()} className="space-y-2">
                  <div className={`text-center p-2 rounded ${
                    isToday ? 'bg-blue-100 text-blue-800 font-semibold' : 'bg-gray-50'
                  }`}>
                    <div className="text-sm font-medium">
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-lg">
                      {day.getDate()}
                    </div>
                  </div>
                  <div 
                    className="min-h-[200px] p-2 border-2 border-dashed border-gray-200 rounded"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, day.toISOString().split('T')[0])}
                  >
                    <div className="space-y-1">
                      {dayItems.map(item => renderScheduledItem(item, true))}
                    </div>
                    {dayItems.length === 0 && (
                      <div className="text-center text-gray-400 py-4 text-xs">
                        Drop items here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className={`grid gap-4 ${selectedDate ? 'grid-cols-1 lg:grid-cols-6' : 'grid-cols-1 lg:grid-cols-4'}`}>
        {/* Unscheduled Items */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Unscheduled Items</CardTitle>
            <Badge variant="outline">
              {unscheduledItems.length} items
            </Badge>
          </CardHeader>
          <CardContent>
            <div 
              className="border-2 border-dashed border-gray-300 rounded p-2 mb-4 text-center text-sm text-gray-500"
              onDragOver={handleDragOver}
              onDrop={handleRemoveFromSchedule}
            >
              Drop here to unschedule
            </div>
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {unscheduledItems.map(item => renderItem(item))}
                {unscheduledItems.length === 0 && (
                  <p className="text-center text-gray-500 py-8">All items are scheduled</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Calendar Grid */}
        <Card className={selectedDate ? "lg:col-span-3" : "lg:col-span-3"}>
          <CardContent className="p-4">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 text-center font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                if (!day) {
                  return <div key={index} className="h-24"></div>;
                }

                const dayItems = getItemsForDate(day);
                const isToday = day.toDateString() === new Date().toDateString();
                const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();

                return (
                  <div
                    key={day.toISOString()}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, day.toISOString().split('T')[0])}
                    onClick={() => handleDateClick(day)}
                    className={`border rounded p-1 h-24 overflow-hidden cursor-pointer transition-colors ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50' 
                        : isToday 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isSelected 
                        ? 'text-purple-600' 
                        : isToday 
                          ? 'text-blue-600' 
                          : 'text-gray-700'
                    }`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-0.5 overflow-y-auto h-16">
                      {dayItems.slice(0, 2).map(item => (
                        <div
                          key={item.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, item)}
                          className={`text-xs p-1 rounded cursor-move ${
                            isCompleted(item)
                              ? 'bg-green-100 text-green-800 line-through opacity-75'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                          title={getItemDisplayName(item)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate flex-1 mr-1">
                              {getItemDisplayName(item)}
                            </span>
                            {item.isWorkOrder && (
                              <div className="flex gap-0.5 flex-shrink-0">
                                {!item.isTKS0026 && (
                                  <>
                                    <div className={`w-1 h-1 rounded-full ${item.kittingComplete ? 'bg-green-400' : 'bg-gray-400'}`} title="Kitting"></div>
                                    <div className={`w-1 h-1 rounded-full ${item.fabricComplete ? 'bg-green-400' : 'bg-gray-400'}`} title="Fabric"></div>
                                  </>
                                )}
                                <div className={`w-1 h-1 rounded-full ${item.metalComplete ? 'bg-green-400' : 'bg-gray-400'}`} title="Metal"></div>
                                <div className={`w-1 h-1 rounded-full ${item.finalAssemblyComplete ? 'bg-green-400' : 'bg-gray-400'}`} title="Assembly"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {dayItems.length > 2 && (
                        <div className="text-xs text-gray-500 p-1">
                          +{dayItems.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Date Detail Sidebar */}
        {selectedDate && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeDateDetail}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <Badge variant="outline">
                {selectedDateItems.length} {selectedDateItems.length === 1 ? 'item' : 'items'}
              </Badge>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {selectedDateItems.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No items scheduled for this date</p>
                    <p className="text-sm mt-2">Drag items from the sidebar to schedule them</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedDateItems.map(item => (
                      <Card key={item.id} className={`${
                        isCompleted(item) 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-white border-gray-200'
                      }`}>
                        <CardContent className="p-4">
                          <div className={`${isCompleted(item) ? 'line-through opacity-75' : ''}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {getItemDisplayName(item)}
                                </h4>
                                <p className="text-sm text-gray-600">Order #{item.baseOrderNumber}</p>
                                <p className="text-sm text-gray-500">{item.customer}</p>
                                <p className="text-sm text-gray-500">{item.location}</p>
                              </div>
                              <Badge className={
                                isCompleted(item) 
                                  ? "bg-green-100 text-green-800" 
                                  : item.status === 'in-process'
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-gray-100 text-gray-800"
                              }>
                                {isCompleted(item) ? 'Complete' : item.status}
                              </Badge>
                            </div>
                            
                            {item.isWorkOrder && (
                              <div className="mt-3">
                                <h5 className="text-sm font-medium text-gray-700 mb-2">Progress:</h5>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  {!item.isTKS0026 && (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${item.kittingComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                        <span>Kitting</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${item.fabricComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                        <span>Fabric</span>
                                      </div>
                                    </>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${item.metalComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                    <span>Metal</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${item.finalAssemblyComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                    <span>Assembly</span>
                                  </div>
                                </div>

                                {item.fabricType && (
                                  <div className="mt-2">
                                    <span className="text-xs text-gray-500">Fabric: {item.fabricType}</span>
                                  </div>
                                )}

                                {item.frameColorVaries ? (
                                  <div className="mt-2 space-y-1">
                                    <div className="text-xs text-gray-500">Frame Colors:</div>
                                    <div className="text-xs space-y-0.5 ml-2">
                                      <div>Housing: {item.housingFrameColor}</div>
                                      <div>Buildout: {item.buildoutFrameColor}</div>
                                      <div>Hem Bar: {item.hemBarFrameColor}</div>
                                      <div>Retention: {item.retentionFrameColor}</div>
                                    </div>
                                  </div>
                                ) : (
                                  item.frameColor && (
                                    <div className="mt-2">
                                      <span className="text-xs text-gray-500">Frame Color: {item.frameColor}</span>
                                    </div>
                                  )
                                )}
                              </div>
                            )}

                            {!item.isWorkOrder && item.details && (
                              <div className="mt-2">
                                <span className="text-xs text-gray-500">Details: {item.details}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
} 